import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

export default function Register() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '+994'
  });
  const [loading, setLoading] = useState(false);
  const { register, user, authReady } = useAuth();
  const navigate = useNavigate();

  // Əgər istifadəçi artıq daxil olubsa, dashboard-a yönləndir
  useEffect(() => {
    if (authReady && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, authReady, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value;
    
    // Həmişə +994 ilə başlasın
    if (!value.startsWith('+994')) {
      value = '+994';
    }
    
    // Yalnız rəqəmləri saxla (+994 sonra)
    const numbers = value.slice(4).replace(/\D/g, '');
    
    // Maksimum 9 rəqəm
    const limitedNumbers = numbers.slice(0, 9);
    
    setFormData({ ...formData, phone: '+994' + limitedNumbers });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Telefon validation
    if (formData.phone.length !== 13) { // +994 + 9 rəqəm
      toast.error('Telefon nömrəsi 9 rəqəmdən ibarət olmalıdır');
      return;
    }
    
    setLoading(true);
    try {
      const result = await register(
        formData.email, 
        formData.password, 
        formData.full_name, 
        formData.phone
      );
      if (result.userData) {
        toast.success('Qeydiyyat uğurla tamamlandı!');
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Register error:', error);
      
      let errorMessage = 'Qeydiyyat uğursuz oldu';
      
      if (error.code === 'auth/network-request-failed') {
        errorMessage = 'İnternet bağlantısı problemi. Yenidən cəhd edin.';
      } else if (error.code === 'permission-denied' || error.message?.includes('permission-denied')) {
        errorMessage = 'Firestore permissions problemi. Firebase Console-da rules yoxlayın.';
      } else if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'Bu email artıq qeydiyyatdan keçib';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Şifrə çox sadədir (minimum 6 simvol)';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Email düzgün formatda deyil';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-4 relative" data-testid="register-page">
      {/* Grid pattern background */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#f0f0f0_1px,transparent_1px),linear-gradient(to_bottom,#f0f0f0_1px,transparent_1px)] bg-[size:20px_20px] opacity-50"></div>
      
      <div className="w-full max-w-md relative z-10">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-black mb-4">I Veb</h1>
            <p className="text-gray-600">Mağazanızı yaradın</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
            <div>
              <Label htmlFor="full_name">Ad və Soyad</Label>
              <Input
                id="full_name"
                name="full_name"
                type="text"
                placeholder="Adınız"
                value={formData.full_name}
                onChange={handleChange}
                required
                data-testid="fullname-input"
              />
            </div>

            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="email@example.com"
                value={formData.email}
                onChange={handleChange}
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="phone">Telefon Nömrəsi (WhatsApp) *</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                placeholder="+994XXXXXXXXX"
                value={formData.phone}
                onChange={handlePhoneChange}
                required
                data-testid="phone-input"
                className="font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">Məsələn: +994557063190 (9 rəqəm, 0 olmadan)</p>
            </div>

            <div>
              <Label htmlFor="password">Şifrə</Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                required
                data-testid="password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-black hover:bg-gray-800 text-white"
              disabled={loading}
              data-testid="register-submit-button"
            >
              {loading ? 'Qeydiyyat edilir...' : 'Qeydiyyatdan keç'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Artıq hesabınız var?{' '}
              <Link to="/login" className="text-black font-medium hover:underline" data-testid="login-link">
                Daxil olun
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-gray-600 hover:text-black" data-testid="home-link">
            ← Ana səhifəyə qayıt
          </Link>
        </div>
      </div>
    </div>
  );
}